//Library Imports
import { useState, useEffect } from "react";
import { Modal, Tabs } from "antd";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

//Local Imports
import { RootState } from "store";
import SmallButton from "../../../Content/Shared/SmallButton";
import { ReactComponent as Cross } from "../../../../assets/img/icons/cross.svg";
import Arrow from "../../../../assets/img/icons/arrow-left.svg";
import { addActionModal, resetSelectedPreviewEmailId, shareProfileConfigurationModal, showUpdateButton } from "../../actions";
import { ControlNameEnum, WFStatus, WFStatusEnum } from "../../interfaces";
import ListsData from "./ListsData";
import IfElseContent from "./IfElseContent";
import WaitModal from "./WaitModal";
import SendEmail from "./SendEmail";
import PreviewEmailAutomation from "modules/Automation/Shared/PreviewEmailAutomation";

//Icons Imports
import AddToListIcon from "../../../../assets/img/icons/addToListIcon.svg";
import ConditionIcon from "../../../../assets/img/icons/conditionIcon.svg";
import DelayIcon from "../../../../assets/img/icons/delayIcon.svg";
import EmailIcon from "../../../../assets/img/icons/emailIcon.svg";
import StopIcon from "../../../../assets/img/icons/stopIcon.svg";

type Props = {};

const AddAction = (props: Props) => {
  // Redux States
  const actionModal = useSelector((state: RootState) => state.automation.modals.addAction);
  const actionModalVisible: boolean = useSelector((state: RootState) => state.automation.modals.addAction.visible);
  const dropAreaData = useSelector((state: RootState) => state.automation.workflow.dropArea);
  const selectedEmail = useSelector((state: RootState) => state.automation.emails.selectedEmail);
  const selectedListsIds: number[] = useSelector((state: RootState) => state.automation.lists.selectedListIds);
  const selectedPreviewEmailId: number = useSelector(
    (state: RootState) => state.automation.emailPreviews.selectedEmailId
  );
  const selectedPreviewCampaignId: number = useSelector(
    (state: RootState) => state.automation.emailPreviews.selectedCampaignId
  );
  const conditionId: number = useSelector((state: RootState) => state.automation.conditionAction.conditionId);
  const conditionEmailId: number = useSelector(
    (state: RootState) => state.automation.conditionAction.conditionEmailId
  );
  const itemData = useSelector((state: RootState) => state.automation.workflow.flow[actionModal?.id]?.detail);
  const showUpdate = useSelector((state: RootState) => state.automation.emails.showUpdate);
  const workflowStatus: WFStatus = useSelector((state: RootState) => state.automation.workflow?.detail?.status);
  const formError = useSelector((state: RootState) => state.automation.emails.form.errors);
  const { first_name, last_name } = useSelector((state: RootState) => state.user.userData);

  // Local States
  const [activeKey, setActiveKey] = useState<any>(ControlNameEnum.SEND_EMAIL);
  const [enableSave, setEnableSave] = useState(false);
  const [updateButton, setUpdateButton] = useState(false); // Storing current state of button for Email Tab
  const [isNewList, setIsNewList] = useState(false);
  const [createNewListName, setCreateNewlistName] = useState<string>("");

  // Local Variables
  const dispatch = useDispatch();
  const listIcons = [EmailIcon, AddToListIcon, DelayIcon, ConditionIcon, StopIcon];
  const fullName = first_name + " " + last_name;
  const error = (formError.emailSubject || formError.sendFrom || (formError.senderName ?? fullName));

  // Component Logic
  useEffect(() => {
    if (actionModal.visible && dropAreaData.hasOwnProperty("controlName")) {
      if (dropAreaData?.controlName === ControlNameEnum.WAIT) enableSaveButtonState(true); // if delay action is dropped, enable save button
      setActiveKey(dropAreaData?.controlName);
    } else if (actionModal.visible) {
      setActiveKey(ControlNameEnum.SEND_EMAIL);
    }
  }, [dropAreaData]);

  useEffect(() => {
    if (actionModal.id) {
      setActiveKey(actionModal.tab);
      if (actionModal.tab === ControlNameEnum.WAIT) enableSaveButtonState(true);
    }
  }, [actionModal.id]);

  useEffect(() => {
    if (!actionModalVisible) {
      enableSaveButtonState(false);
    }
  }, [actionModalVisible]);

  useEffect(() => {
    if (showUpdate) {
      if (error) {
        setUpdateButton(true);
      } else {
        setUpdateButton(false);
      }
    }
  }, [showUpdate]);

  useEffect(() => {
    if (activeKey !== ControlNameEnum.SEND_EMAIL) {
      // Update button changed to Save button for other tabs
      showUpdateButton(false);
    } else {
      // For Email tab, the email selected will decide the enabling and text of the button
      if (updateButton) {
        showUpdateButton(updateButton);
        enableSaveButtonState(false);
      }
      if (selectedEmail.id !== 0 && error) {
        enableSaveButtonState(false);
        showUpdateButton(true);
      } else if (selectedEmail.id !== 0 && !error) {
        enableSaveButtonState(true);
        if (updateButton) {
          showUpdateButton(true);
        } else {
          showUpdateButton(false);
        }
      }
    }
  }, [activeKey]);

  let isTabDisabled = Boolean(actionModal.id);

  const enableSaveButtonState = (value: boolean) => {
    setEnableSave(value);
  };

  const tabPanels = [
    {
      name: "Email",
      key: ControlNameEnum.SEND_EMAIL,
      disabled: isTabDisabled,
      className: "email-tab",
      component: SendEmail,
    },
    {
      name: "Add to a list",
      key: ControlNameEnum.ADD_LIST,
      disabled: isTabDisabled,
      className: "",
      component: ListsData,
    },
    {
      //* WF Name changes done here
      name: "Delay",
      key: ControlNameEnum.WAIT,
      disabled: isTabDisabled,
      className: "wait-tab",
      component: WaitModal,
    },
    {
      //* WF Name changes done here
      name: "Condition",
      key: ControlNameEnum.IF_ELSE,
      disabled: isTabDisabled,
      className: "",
      component: IfElseContent,
    },
  ];

  const handleCancel = () => {
    enableSaveButtonState(false);
    dispatch(addActionModal(false));
    setIsNewList(false);
    setCreateNewlistName("");
  };


  const tabChangeHandler = (key: any) => {
    setActiveKey(key);
    // in case of delay save button will be enabled by default
    if (key === ControlNameEnum.WAIT) enableSaveButtonState(true);
    else if (key === ControlNameEnum.SEND_EMAIL && selectedEmail.id > 0) enableSaveButtonState(true);
    else if (key === ControlNameEnum.ADD_LIST && selectedListsIds.length > 0) enableSaveButtonState(true);
    else if (key === ControlNameEnum.IF_ELSE && conditionId > 0 && conditionEmailId > 0)
      enableSaveButtonState(true);
    else enableSaveButtonState(false);
  };

  const configureClickHandler = () => {
    dispatch(shareProfileConfigurationModal(true));
  };
  const newListHandler = () => {
    setIsNewList(true);
  };

  return (
    <>
      <StyledModal
        centered
        visible={actionModal.visible}
        // onOk={setIsShow}
        onCancel={handleCancel}
        footer={null}
        closeIcon={<Cross />}
        width={980}
        wrapClassName="workflow-modal"
        title="Add New Action"
        bodyStyle={{ padding: "0px" }}
        maskClosable={false}
      // height={'70vh'}
      >
        <Wrapper triggerEmail={actionModal.from}>
          <div className="add-action-wrapper">
            <Tabs
              onChange={(key: any) => tabChangeHandler(key)}
              activeKey={`${actionModal.from === "trigger" ? ControlNameEnum.SEND_EMAIL : activeKey}`}
              defaultActiveKey={ControlNameEnum.SEND_EMAIL}
              tabPosition="left"
            >
              {tabPanels.map((tab, index) => {
                const Component = tab.component;
                return (
                  <Tabs.TabPane
                    tab={
                      <IconAndTitleWrapper>
                        <img src={listIcons[index]} />
                        <span>{tab.name}</span>
                      </IconAndTitleWrapper>
                    }
                    key={tab.key}
                    disabled={tab.disabled}
                    className={`${tab.className} ${tab.key === ControlNameEnum.ADD_LIST
                      ? "add-to-list"
                      : tab.key === ControlNameEnum.SEND_EMAIL
                        ? "send-email-container"
                        : ""
                      // : tab.key === ControlNameEnum.WAIT ? "":""
                      }`}
                  >
                    <Component
                      id={actionModal.id}
                      enableSaveButton={enableSaveButtonState}
                      isNewList={isNewList}
                      setIsNewList={setIsNewList}
                      createNewListName={createNewListName}
                      setCreateNewlistName={setCreateNewlistName}
                    />
                  </Tabs.TabPane>
                );
              })}
            </Tabs>

            <div className="d-flex justify-content-end save-btn">
              {activeKey === ControlNameEnum.ADD_LIST ? (
                <span className="new-list-btn" onClick={newListHandler}>
                  Create new list
                </span>
              ) : null}

              {selectedEmail.id === 0.5 && activeKey === ControlNameEnum.SEND_EMAIL ? (
                <SmallButton
                  // disabled={!enableSave}
                  title={
                    actionModal.id === "" || itemData.configurations === undefined
                      ? "Configure Share Profile"
                      : "Edit Share Profile configuration"
                  }
                  onClick={configureClickHandler}
                // type="submit"
                // form={`${activeKey}`} // On press the Save button, form with same id as in 'activeKey', 'submit' Handler will be called
                // // reason of doing is to separate out the logic in their specific component while using the same 'Save' button
                />
              ) : (
                <SmallButton
                  disabled={!enableSave}
                  title={showUpdate ? "Update" : "Save"}
                  type="submit"
                  form={`${actionModal.from === "trigger" ? ControlNameEnum.SEND_EMAIL : activeKey}`} // On press the Save button, form with same id as in 'activeKey', 'submit' Handler will be called
                // reason of doing is to separate out the logic in their specific component while using the same 'Save' button
                />
              )}
            </div>
          </div>
        </Wrapper>
      </StyledModal>
      {/* showing preview email here, as styles of tabs in modal
        overlaps with that of preview, I have made them sibling nodes
      */}
      {selectedPreviewEmailId > 0 ? (
        <PreviewWrapper>
          <div className="backoverlay" />
          <div className="preview-screen active">
            <div className="preview-label">Email Preview</div>
            <PreviewEmailAutomation
              emailId={selectedPreviewEmailId}
              campaignId={selectedPreviewCampaignId}
              assetType="1"
              goBack={resetSelectedPreviewEmailId}
            />
            <span className="close-preview" onClick={resetSelectedPreviewEmailId}>
              <Cross />
            </span>
          </div>
        </PreviewWrapper>
      ) : null}
    </>
  );
};

export default AddAction;

type WrapperProps = {
  triggerEmail?: string;
};

const Wrapper = styled.div<WrapperProps>`
  .add-action-wrapper {
    .ant-tabs {
      overflow: visible;
      .ant-modal-header {
        border-radius: 8px 8px 0 0;
        font-weight: 500;
        font-family: "Roboto", sans-serif;
        font-size: 24px;
        line-height: 38px;
        letter-spacing: -0.666667px;
        color: #5e7079;
      }
      .ant-tabs-nav {
        width: 265px;
        height: 100%;
        margin-bottom: 24px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          width: 210px;
        }
        .ant-tabs-ink-bar-animated,
        .ant-tabs-nav-operations {
          display: none;
        }
        .ant-tabs-nav-list > :not(.ant-tabs-tab-active){
          opacity: ${(props) => props.triggerEmail === "trigger" ? "0.5" : "1"};
          pointer-events: ${(props) => props.triggerEmail === "trigger" ? "none !important" : "auto"};
        }
        .ant-tabs-nav-list {
          .ant-tabs-tab {
            position: relative;
            margin-top: 0;
            padding: 10px 24px;
            .ant-tabs-tab-btn {
              color: #424b50;
              font-size: 16px;
              font-weight: 500;
              font-family: "Roboto", sans-serif;
              @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                font-size: 12px;
              }
            }
            &.ant-tabs-tab-active,
            &:hover {
              background-color: #e7f6ea;
              .ant-tabs-tab-btn {
                color: #424b50;
                text-shadow: none;
              }
            }
            &:after {
              content: "";
              color: #424b50;
              position: absolute;
              top: 18px;
              right: 18px;
              background-image: url("${Arrow}");
              background-repeat: no-repeat;
              background-size: contain;
              width: 5px;
              height: 11px;
              @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                top: 14px;
              }
            }
          }
        }
      }
      .ant-tabs-content-holder {
        border-left: 1px solid transparent;
        margin: 0;
        position: relative;
        &:before {
          content: "";
          width: 1px;
          height: calc(100% + 65px);
          position: absolute;
          top: 0;
          left: 0;
          background-color: #dddddd;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            height: calc(100% + 54px);
          }
        }
        .ant-tabs-tabpane {
          min-height: 553px;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            min-height: 462px;
            font-size: 16px;
          }
          &.send-email-container {
            padding-left: 0;
          }
          .label-model {
            color: #5e7079;
            font-size: 24px;
            font-weight: 500;
            font-family: "Roboto", sans-serif;
            margin: 10px 0;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              font-size: 16px;
            }
          }
          .overflow-scroll {
            width: 100% !important;
            height: 480px;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              height: 400px;
            }
            .search-wrapper {
              max-width: 250px;
              padding-right: 0;
              margin: 0;
            }
          }
          &.email-tab {
            padding-left: 1px;
          }
          .condition-wrapper,
          .wait-wrapper {
            width: calc(100% - 50px);
            .label-condition {
              color: #5e7079;
              font-size: 20px;
              font-weight: 500;
              font-family: "Roboto Slab", serif;
              margin-bottom: 16px;
              @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                font-size: 14px;
              }
            }
            .add-condition-wrapper {
              /* cursor: pointer; */
              color: #27ce0a;
              font-size: 14px;
              font-weight: 700;
              font-family: "Roboto", sans-serif;
              margin: 8px 0;
              @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                font-size: 12px;
              }
              .add-condition-button {
                width: 160px;
                cursor: pointer;
                img {
                  height: 11px;
                  vertical-align: baseline;
                }
              }
            }
            label {
              margin: 0 !important;
            }
            div[class$="-control"] {
              min-width: 100%;
              padding-left: 2px;
              height: 44px;
              border-color: #dadada;
              @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                height: 32px;
              }
            }
            input {
              border-color: #dadada;
              padding-right: 15px;
              height: 100%;
            }
            .condition-dropdown {
              border-color: #dadada;
              box-sizing: border-box;
              height: 44px;
              margin-bottom: 16px;
              @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                border-radius: 6px;
                height: auto;
              }
              &.ant-select-open {
                cursor: text;
              }
              .ant-select-selector {
                border-radius: 7px;
                height: 44px;
                @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                  border-radius: 6px;
                  height: auto;
                }
                .ant-select-selection-item {
                  max-width: 210px;
                  overflow: hidden;
                  @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                    max-width: 250px;
                  }
                }
              }
              .ant-select-selection-placeholder {
                text-overflow: ellipsis;
                white-space: nowrap;
                color: #7e7e7e;
                font-size: 16px;
                line-height: 44px;
                @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                  font-size: 12px;
                  line-height: 28px;
                }
              }
              .ant-select-arrow {
                border-left: 1px solid hsl(0, 0%, 80%);
                padding-left: 12px;
                margin-right: 5px;
                .anticon-down {
                  width: 0;
                  height: 0;
                  border-left: 6px solid transparent;
                  border-right: 6px solid transparent;
                  border-top: 8px solid #7e7e7e;
                  svg {
                    opacity: 0;
                  }
                }
                .anticon-search {
                  /* width: 20px;
                  height: 20px; */
                  svg {
                    path {
                      fill: #7e7e7e;
                    }
                  }
                }
              }
              .ant-select-clear {
                top: 20px;
                height: 20px;
                @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                  top: 15px;
                }
              }
            }
            &.link-condition {
              .ant-select-selection-item {
                display: inline-block;
                line-height: 36px;
              }
            }
          }
          .search-wrapper {
            margin-top: 8px;
            margin-bottom: 6px;
            padding-right: 24px;
            width: 100%;
            float: left;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              margin-top: 6px;
              margin-bottom: 4px;
            }
          }
          form {
            position: relative;
            .list-count {
              position: absolute;
              right: 25px;
              top: 70px;
              font-family: "Roboto";
              font-style: normal;
              font-weight: 500;
              font-size: 16px;
              line-height: 18px;
              text-align: right;
              letter-spacing: -0.333333px;
              color: #1ec252;
              @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                top: 60px;
                font-size: 14px;
              }
            }
          }
          .email-list {
            list-style-type: none;
            margin: 0;
            padding: 0;
            width: calc(100% - 10px);
            float: left;
            height: 499px;
            overflow-y: auto;
            scrollbar-width: thin;
            scrollbar-color: #001760 #efefef;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              height: 400px;
              /* height: 320px; */
              /* min-height: 300px; */
            }
            &::-webkit-scrollbar {
              width: 5px;
              height: 5px;
              border-radius: 5px;
            }
            /* Track */
            &::-webkit-scrollbar-track {
              background: #fff;
              margin-top: 0;
              margin-bottom: 10px;
              @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                margin-top: 0;
              }
            }
            /* Handle */
            &::-webkit-scrollbar-thumb {
              background: #001760;
            }
            li {
              margin: 8px 0;
              padding: 0;
              max-width: calc(100% - 150px);
              &.input-li {
                max-width: 100%;
                margin-right: 12px;
                padding-left: 2px;
              }
              span {
                color: #7e7e7e;
                font-size: 16px;
                font-weight: 500;
                font-family: "Roboto", sans-serif;
                @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                  font-size: 12px;
                }
              }
              .ant-typography {
                color: #7e7e7e;
                font-size: 16px;
                font-weight: 500;
                font-family: "Roboto", sans-serif;
                -webkit-line-clamp: 1;
                overflow: hidden;
                white-space: nowrap;
                margin-bottom: 0;
                @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                  font-size: 12px;
                  padding-top: 2px;
                  line-height: 1;
                }
              }
              .hidden-text {
                height: 1px;
                max-width: 50%;
                overflow: hidden;
                opacity: 0;
                visibility: hidden;
                white-space: nowrap;
                line-height: 1px;
                padding: 0;
                margin: 0;
                float: left;
                margin-top: -1px;
              }
            }
          }
        }
      }
    }
    .save-btn {
      padding: 12px 24px 14px 24px;
      .new-list-btn {
        color: #27ce0a;
        font-family: "Roboto", sans-serif;
        font-style: normal;
        font-weight: 500;
        font-size: 14px;
        margin: auto 16px;
        /* padding: 7px 0; */
        cursor: pointer;
      }
    }
    .add-to-list {
      min-height: 300px;
      > div:nth-child(2) {
        float: none;
        width: 100%;
        justify-content: flex-start;
        .inner-wrapper {
          width: 100%;
          .description {
            text-align: center;
          }
        }
      }
    }
  }
`;

const StyledModal = styled(Modal)`
  height: 700px;
  @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
    height: 600px;
  }

  .ant-modal-content {
    .ant-modal-header {
      border-top-left-radius: 8px;
      border-top-right-radius: 8px;
      .ant-modal-title {
        margin: 0;
        font-family: "Roboto";
        color: #424b50;
        font-weight: 500;
        font-size: 16px;
        line-height: 22px;
        word-wrap: break-word;
      }
    }
    border-radius: 8px;
    box-shadow: 0px 0px 17px -2px rgba(0, 0, 0, 0.0821427);
  }
`;

const PreviewWrapper = styled.div`
  .backoverlay {
    height: 100vh;
    width: 100vw;
    background: black;
    z-index: 1998;
    position: fixed;
    top: 0;
    left: 0;
    opacity: 0.5;
  }
  .preview-screen {
    height: 100vh;
    background-color: #f9f8f6;
    z-index: -999;
    display: none;
    padding-right: 15px;
    transition: 0.5s;
    position: fixed;
    top: 0;
    left: 88px;
    width: calc(100% - 88px);
    padding-top: 25px;
    border-right: 1px solid #b5c3d4;
    overflow: auto;
    box-sizing: border-box;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      width: calc(100% - 63px);
      left: 63px;
    }
    &.active {
      z-index: 1999;
      display: block;
    }
    /* .ant-tabs-card{
      height: calc(100vh - 25px);
    } */
    .ant-tabs-nav {
      .ant-tabs-nav-list {
        transform: translate(-50%, 0px) !important;
        left: 50% !important;
        .ant-tabs-tab {
          margin-left: 0px;
          border-radius: 8px 0 0 8px;
          font-family: "robotomedium";
          border: 1px solid rgb(218, 218, 218);
          color: rgb(126, 126, 126);
          font-size: 16px;
          font-weight: 500;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            border-radius: 6px 0 0 6px;
            padding: 8px 12px;
            font-size: 12px;
            line-height: 14px;
          }
          + .ant-tabs-tab {
            margin-left: 0px;
            border-radius: 0 8px 8px 0;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              border-radius: 0 6px 6px 0;
            }
          }
          &.ant-tabs-tab-active {
            background: #def6e6;
          }
        }
      }
    }
    .preview-label {
      position: absolute;
      top: 20px;
      left: 25px;
      color: #424b50;
      font-family: "Roboto Slab", serif;
      font-weight: 500;
      font-size: 32px;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 24px;
        top: 25px;
      }
    }
    .close-preview {
      position: absolute;
      top: 30px;
      right: 20px;
      z-index: 9;
      cursor: pointer;
    }
  }
`;

const IconAndTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  & span {
    padding: 2px 0 0 9px;
  }
`;
